import { useCallback, useState } from "react";
import { usePermissions } from "@/lib/permissions";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { alertService, vehicleService, driverService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SeverityBadge } from "@/components/common/StatusBadge";
import { SkeletonList } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { LoadMoreSentinel } from "@/components/common/LoadMoreSentinel";
import { relativeTime, dateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wrench, Cpu, Hexagon, Gauge, Check, CheckCheck, MapPin, Bell, CloudLightning, CalendarClock, Loader2, ParkingSquare,
} from "lucide-react";

// Keys must match `alert_types.category` in the backend exactly (see
// backend/app/models — categories are "vehicle_health", "device",
// "geofence", "driving", "weather"). A mismatch here used to crash this
// page: an unmapped category made `CATEGORY[a.category]` undefined, and
// reading `.icon` off that threw during render.
const CATEGORY = {
  vehicle_health: { label: "Vehicle Health", icon: Wrench },
  device: { label: "Device", icon: Cpu },
  geofence: { label: "Geofence", icon: Hexagon },
  driving: { label: "Driving", icon: Gauge },
  weather: { label: "Weather", icon: CloudLightning },
  parking: { label: "Parking", icon: ParkingSquare },
};
const DEFAULT_CATEGORY = { label: "Alert", icon: Bell };

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "acknowledged", label: "Acknowledged" },
  { key: "resolved", label: "Resolved" },
];
const CAT_FILTERS = [
  { key: "all", label: "All categories" },
  { key: "vehicle_health", label: "Vehicle Health" },
  { key: "device", label: "Device" },
  { key: "geofence", label: "Geofence" },
  { key: "driving", label: "Driving" },
  { key: "weather", label: "Weather" },
  { key: "parking", label: "Parking" },
];

export default function Alerts() {
  const { isOperator } = usePermissions();
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [ackingAll, setAckingAll] = useState(false);

  // Page 1 is the live table (~last 20 days); every page after walks
  // further back through alert_archive in 20-day windows — see the matching
  // comment in Trips.jsx for why the archival split makes this safe. Status
  // isn't a server-side filter on the archive endpoint (mirrors how status/
  // driverId are already client-side-only filters on tripService.list), so
  // it's applied here the same way for archived pages too.
  const fetchPage = useCallback(async (cursor) => {
    if (cursor === undefined) {
      const alerts = await alertService.list({ status, category });
      return { items: alerts, nextCursor: new Date().toISOString(), hasMore: true };
    }
    const { alerts, windowStart, hasMore } = await alertService.archive({ category, before: cursor, windowDays: 20 });
    const filtered = status === "all" ? alerts : alerts.filter((a) => a.status === status);
    return { items: filtered, nextCursor: windowStart, hasMore };
  }, [status, category]);

  const { items: alerts, setItems, loading, loadingMore, hasMore, loadMore } = useInfiniteScroll(fetchPage, [status, category]);

  const act = async (id, kind) => {
    try {
      if (kind === "ack") await alertService.acknowledge(id);
      else await alertService.resolve(id);
      setItems((prev) => prev.map((a) => (a.id === id ? { ...a, status: kind === "ack" ? "acknowledged" : "resolved" } : a)));
      toast.success(kind === "ack" ? "Alert acknowledged" : "Alert resolved");
    } catch {
      toast.error(`Failed to ${kind === "ack" ? "acknowledge" : "resolve"} alert`);
    }
  };

  const ackAll = async () => {
    const targets = alerts.filter((a) => a.status === "new");
    if (targets.length === 0) return;
    setAckingAll(true);
    const results = await Promise.allSettled(targets.map((a) => alertService.acknowledge(a.id)));
    setAckingAll(false);
    const failedIds = new Set(targets.filter((_, i) => results[i].status === "rejected").map((a) => a.id));
    setItems((prev) => prev.map((a) => (a.status === "new" && !failedIds.has(a.id) ? { ...a, status: "acknowledged" } : a)));
    const failed = failedIds.size;
    if (failed) toast.error(`Acknowledged ${targets.length - failed}, ${failed} failed`);
    else toast.success("All new alerts acknowledged");
  };

  const counts = { new: (alerts || []).filter((a) => a.status === "new").length };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Alerts"
        subtitle="Vehicle health, device, geofence and driving alerts across the fleet."
        actions={isOperator && (
          <Button variant="secondary" disabled={ackingAll} onClick={ackAll}><CheckCheck className="mr-1.5 h-4 w-4" /> {ackingAll ? "Acknowledging…" : "Acknowledge all"}</Button>
        )}
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1.5">
          {STATUS_TABS.map((s) => (
            <button key={s.key} onClick={() => setStatus(s.key)}
              className={cn("rounded-full border px-3 py-1.5 text-sm font-medium transition", status === s.key ? "border-primary/40 bg-primary/15 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground")}
              data-testid={`alerts-status-${s.key}`}>
              {s.label}{s.key === "new" && counts.new > 0 && <span className="ml-1.5 text-xs">({counts.new})</span>}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CAT_FILTERS.map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)}
              className={cn("rounded-full border px-2.5 py-1 text-xs font-medium transition", category === c.key ? "border-border bg-secondary text-foreground" : "border-border/60 text-muted-foreground hover:text-foreground")}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <SkeletonList rows={6} /> : alerts.length === 0 ? (
        <EmptyState icon={Bell} title="No active alerts" description="No alerts match the selected filters. Your fleet is running smoothly." />
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => {
            // Falls back rather than crashing the whole page if a future
            // alert_type ever ships a category the map above doesn't know
            // about yet.
            const cat = CATEGORY[a.category] || DEFAULT_CATEGORY;
            const Icon = cat.icon;
            const v = vehicleService.byId(a.vehicleId);
            const d = a.driverId ? driverService.byId(a.driverId) : null;
            return (
              <div key={a.id} data-testid={`alert-${a.id}`} className={cn("flex flex-col gap-3 rounded-2xl border bg-card/50 p-4 transition hover:bg-card/70 sm:flex-row sm:items-center", a.severity === "critical" && a.status === "new" ? "border-red-500/25" : "border-border/70")}>
                <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border", a.severity === "critical" ? "border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-400" : a.severity === "warning" ? "border-amber-400/20 bg-amber-400/10 text-amber-600 dark:text-amber-400" : "border-sky-400/20 bg-sky-400/10 text-sky-600 dark:text-sky-400")}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{cat.label} Alert</span>
                    <SeverityBadge severity={a.severity} />
                  </div>
                  <div className="mt-0.5 font-display text-base font-medium">{a.type}</div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{a.description}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="text-foreground/80">{v?.name}</span>
                    {d && <span>· {d.name}</span>}
                    {/* When it happened, first — an alert log is read to place
                        an event in time. The relative age follows as a quick
                        "how stale is this", and the position is shown only
                        when the alert actually recorded one of its own. */}
                    <span className="inline-flex items-center gap-1 text-foreground/80"><CalendarClock className="h-3 w-3" /> {dateTime(a.time)}</span>
                    <span>· {relativeTime(a.time)}</span>
                    {a.geofenceName && <span className="inline-flex items-center gap-1"><Hexagon className="h-3 w-3" /> {a.geofenceName}</span>}
                    {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {a.status === "new" && <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300">New</span>}
                  {a.status === "acknowledged" && <span className="rounded-full bg-amber-400/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">Acknowledged</span>}
                  {a.status === "resolved" && <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">Resolved</span>}
                  {isOperator && a.status === "new" && <Button size="sm" variant="secondary" onClick={() => act(a.id, "ack")} data-testid={`ack-${a.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Ack</Button>}
                  {isOperator && a.status !== "resolved" && <Button size="sm" variant="secondary" onClick={() => act(a.id, "resolve")} data-testid={`resolve-${a.id}`}>Resolve</Button>}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <>
              <LoadMoreSentinel onVisible={loadMore} disabled={loadingMore} testId="alerts-load-more" />
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading older alerts…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
